// src/components/parent-dashboard/views/ChildDetailView.js
import React from "react";
import {
    ArrowLeft,
    BarChart3,
    BookOpen,
    Calendar,
    CheckCircle,
    FileText,
    User,
    Users,
    X,
    AlertCircle,
    Mail,
    Clock,
    ChevronRight
} from "lucide-react";
import {
    GraduationCap,
    Scale,
    Brain,
    MessageSquareText,
    Brush,
    HeartHandshake
} from 'lucide-react';
import { getInitials, formatSimpleDate, calculateAttendanceStats } from "../utils/formatters";
import ProfilePictureModal from "../modals/ProfilePictureModal";

/**
 * Child Detail View Component
 * Shows detailed information about a selected child including:
 * - Academic progress
 * - Classes
 * - Teachers
 * - Assignments
 * - Attendance overview
 */
const ChildDetailView = ({
    selectedChild,
    childProfilePicture,
    selectedChildClasses,
    selectedChildTeachers,
    selectedChildProgress,
    selectedChildAssignments,
    selectedChildAttendance,
    handleBack,
    handleClassSelect,
    handleAssignmentSelect,
    showProfilePictureModal,
    setShowProfilePictureModal,
    handleProfilePictureUpload,
    uploadingPicture,
    loading,
    error,
    API_BASE_URL
}) => {
    // Calculate attendance statistics
    const stats = calculateAttendanceStats(selectedChildAttendance);

    // Function to get a relevant icon based on the class name/domain
    const getClassIcon = (className) => {
        const lowerCaseName = className.toLowerCase();
        if (lowerCaseName.includes("physical development")) {
            return <Scale size={28} className="text-[#2173B7]"/>;
        } else if (lowerCaseName.includes("socio-emotional development")) {
            return <HeartHandshake size={28} className="text-[#2173B7]"/>;
        } else if (lowerCaseName.includes("cognitive development")) {
            return <Brain size={28} className="text-[#2173B7]"/>;
        } else if (lowerCaseName.includes("language & literacy development")) {
            return <MessageSquareText size={28} className="text-[#2173B7]"/>;
        } else if (lowerCaseName.includes("aesthetic & cultural development")) {
            return <Brush size={28} className="text-[#2173B7]"/>;
        } else if (lowerCaseName.includes("positive learning habits")) {
            return <GraduationCap size={28} className="text-[#2173B7]"/>;
        }
        return <GraduationCap size={28} className="text-[#2173B7]"/>; // Default icon
    };

    return (
        <div className="min-h-screen bg-[#F7FAFF] font-[poppins]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header with navigation */}
                <div className="mb-6">
                    <button
                        onClick={handleBack}
                        className="inline-flex items-center text-sm font-medium text-[#D67C2C] hover:text-[#1a5b94]"
                    >
                        <ArrowLeft size={16} className="mr-1"/>
                        Back to Dashboard
                    </button>

                    <div className="mt-3 flex flex-wrap items-center justify-between">
                        <div className="flex items-center">
                            {/* Avatar with profile picture support */}
                            <div className="relative">
                                {childProfilePicture ? (
                                    <img
                                        src={childProfilePicture}
                                        alt={selectedChild.name}
                                        className="h-12 w-12 rounded-full object-cover border-2 border-[#F7D1B6]"
                                    />
                                ) : (
                                    <div
                                        className="h-12 w-12 rounded-full bg-[#FFF8F4] flex items-center justify-center text-xl text-[#2173B7] font-medium border-2 border-[#F7D1B6]">
                                        {getInitials(selectedChild.name)}
                                    </div>
                                )}
                            </div>
                            <div className="ml-4">
                                <h1 className="text-2xl font-bold text-[#D67C2C]">{selectedChild.name}</h1>
                                <p className="text-sm text-gray-600">
                                    {selectedChild.email} • {selectedChild.grade ? `Grade ${selectedChild.grade}` : "No grade information"}
                                </p>
                            </div>
                        </div>

                        {/* Profile picture upload button in top right */}
                        <div>
                            <button
                                onClick={() => setShowProfilePictureModal(true)}
                                className="inline-flex items-center px-4 py-2 border border-[#2173B7] shadow-sm text-sm font-medium rounded-lg text-[#2173B7] bg-white hover:bg-[#FFF8F4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2173B7]"
                            >
                                <User size={16} className="mr-2"/>
                                {childProfilePicture ? "Change Picture" : "Add Picture"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-500"/>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="flex justify-center items-center py-12">
                        <div
                            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D67C2C]"></div>
                    </div>
                )}

                {/* Content sections */}
                {!loading && (
                    <div className="space-y-8">
                        {/* Progress Section */}
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                            <div
                                className="px-4 py-5 sm:px-6 bg-[#FFF8F4] flex justify-between items-center border-b-2 border-[#F7D1B6]">
                                <div>
                                    <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Academic
                                        Progress</h2>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        Overall academic performance and achievements
                                    </p>
                                </div>
                                <BarChart3 className="h-6 w-6 text-[#2173B7]"/>
                            </div>

                            <div className="px-4 py-5 sm:p-6">
                                {!selectedChildProgress ? (
                                    <div className="text-center py-6">
                                        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                                        <p className="text-gray-500">No progress data available yet</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="bg-blue-50 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-medium text-blue-900">Total XP</h3>
                                                <span className="h-5 w-5 text-blue-600">✨</span>
                                            </div>
                                            <p className="mt-1 text-2xl font-bold text-blue-700">{selectedChildProgress.total_xp}</p>
                                            <p className="mt-1 text-xs text-blue-700">Points earned from
                                                activities</p>
                                        </div>

                                        <div className="bg-green-50 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-medium text-green-900">Quiz
                                                    Score</h3>
                                                <span className="h-5 w-5 text-green-600">🏆</span>
                                            </div>
                                            <p className="mt-1 text-2xl font-bold text-green-700">{selectedChildProgress.average_quiz_score}%</p>
                                            <p className="mt-1 text-xs text-green-700">Average quiz
                                                performance</p>
                                        </div>

                                        <div className="bg-yellow-50 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-medium text-yellow-900">Quizzes</h3>
                                                <FileText className="h-5 w-5 text-yellow-600"/>
                                            </div>
                                            <p className="mt-1 text-2xl font-bold text-yellow-700">{selectedChildProgress.quizzes_completed}</p>
                                            <p className="mt-1 text-xs text-yellow-700">Quizzes completed</p>
                                        </div>

                                        <div className="bg-purple-50 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-medium text-purple-900">Lessons</h3>
                                                <BookOpen className="h-5 w-5 text-purple-600"/>
                                            </div>
                                            <p className="mt-1 text-2xl font-bold text-purple-700">{selectedChildProgress.lessons_completed}</p>
                                            <p className="mt-1 text-xs text-purple-700">Lessons completed</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Classes Section */}
                        <div className="bg-white shadow-xl rounded-2xl overflow-hidden mb-8">
                            <div
                                className="px-4 py-5 sm:px-6 bg-[#FFF8F4] flex justify-between items-center border-b-2 border-[#F7D1B6]">
                                <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Enrolled
                                    Classes</h2>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                    Classes your child is currently enrolled in
                                </p>
                            </div>

                            <div className="p-6">
                                {selectedChildClasses.length === 0 ? (
                                    <div className="p-8 text-center text-gray-600 bg-gray-50 rounded-lg">
                                        <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4"/>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Enrolled
                                            Classes Yet!</h3>
                                        <p className="text-lg">
                                            It looks like your child isn't enrolled in any classes at the
                                            moment.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {selectedChildClasses.map((classItem) => (
                                            <div
                                                key={classItem.class_id}
                                                className="relative group p-6 rounded-xl shadow-md border-2 border-[#F7D1B6] bg-white
                                           hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                                                onClick={() => handleClassSelect(classItem)}
                                            >
                                                <div className="flex items-start mb-4">
                                                    {/* Icon Circle with Light Orange Background */}
                                                    <div
                                                        className="flex-shrink-0 h-12 w-12 rounded-full bg-[#FFF8F4] flex items-center justify-center mr-4">
                                                        {getClassIcon(classItem.name)}
                                                    </div>
                                                    {/* Class Name (Blue) and Description (Orange) */}
                                                    <div>
                                                        <h3 className="text-xl font-bold text-[#D67C2C] leading-tight">
                                                            {classItem.name}
                                                        </h3>
                                                        <p className="text-sm text-[#2173B7] mt-1">
                                                            {classItem.description || "No description provided."}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Grade (Orange) and Teacher (Gray/Orange) */}
                                                {classItem.grade && (
                                                    <p className="text-sm text-[#2173B7] mb-2">
                                                        Grade: <span
                                                        className="font-semibold">{classItem.grade}</span>
                                                    </p>
                                                )}
                                                <div className="flex items-center text-gray-600">
                                                    <User
                                                        className="h-4 w-4 mr-2 text-[#2173B7]"/>
                                                    <p className="text-sm text-[#2173B7]">{classItem.teacher?.name || "Teacher not assigned"}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Teachers Section */}
                        <div className="bg-white shadow-xl rounded-2xl overflow-hidden mb-8">
                            <div
                                className="px-4 py-5 sm:px-6 bg-[#FFF8F4] flex justify-between items-center border-b-2 border-[#F7D1B6]">
                                <div>
                                    <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Teachers</h2>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        Current teachers for all enrolled classes
                                    </p>
                                </div>
                                {/* Teacher Count Pill */}
                                <div
                                    className="bg-[#FFF8F4] rounded-full px-4 py-2 text-sm font-medium text-[#2173B7] border border-[#2173B7] shadow-sm">
                                    {selectedChildTeachers.length} Teacher{selectedChildTeachers.length !== 1 ? 's' : ''}
                                </div>
                            </div>

                            <div className="p-6">
                                {selectedChildTeachers.length === 0 ? (
                                    <div className="p-8 text-center text-gray-600 bg-gray-50 rounded-lg">
                                        <Users className="h-16 w-16 text-gray-400 mx-auto mb-4"/>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Teachers
                                            Assigned Yet!</h3>
                                        <p className="text-lg">
                                            Teachers will appear here once your child is enrolled in classes.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {selectedChildTeachers.map((teacher) => (
                                            <div
                                                key={teacher.teacher_id}
                                                className="relative group p-6 rounded-xl shadow-md border-2 border-[#F7D1B6] bg-white
                                           hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                                            >
                                                <div className="flex items-start mb-4">
                                                    {/* Initials Circle with Light Orange Background */}
                                                    <div
                                                        className="flex-shrink-0 h-12 w-12 rounded-full bg-[#FFF8F4] flex items-center justify-center text-[#2173B7] font-medium text-lg mr-4 border border-[#F7D1B6]">
                                                        {getInitials(teacher.name)}
                                                    </div>
                                                    {/* Teacher Name (Blue) */}
                                                    <div>
                                                        <h3 className="text-xl font-bold text-[#D67C2C] leading-tight">
                                                            {teacher.name}
                                                        </h3>
                                                    </div>
                                                </div>

                                                {/* Teacher Email (Orange) */}
                                                <div className="flex items-center text-[#2173B7]">
                                                    <Mail className="h-4 w-4 mr-2 text-[#2173B7]"/>
                                                    <p className="text-sm">{teacher.email || "No email provided"}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Assignments Section */}
                        <div className="bg-white shadow-xl rounded-2xl overflow-hidden mb-8">
                            <div
                                className="px-4 py-5 sm:px-6 bg-[#FFF8F4] flex justify-between items-center border-b-2 border-[#F7D1B6]">
                                <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Assignments</h2>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                    Current and recent assignments
                                </p>
                            </div>

                            <div className="p-6">
                                {selectedChildAssignments.length === 0 ? (
                                    <div className="p-8 text-center text-gray-600 bg-gray-50 rounded-lg">
                                        <span className="h-16 w-16 text-gray-400 mx-auto mb-4 block">📋</span>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Assignments
                                            Yet!</h3>
                                        <p className="text-lg">
                                            Your child doesn't have any assignments at the moment.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {selectedChildAssignments.map((assignment) => (
                                            <div
                                                key={assignment.assignment_id}
                                                className="relative group p-6 rounded-xl shadow-md border-2 border-[#F7D1B6] bg-white
                                           hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                                                onClick={() => handleAssignmentSelect(assignment)}
                                            >
                                                <div className="flex items-start mb-4">
                                                    {/* Icon Circle with Light Orange Background */}
                                                    <div
                                                        className="flex-shrink-0 h-12 w-12 rounded-full bg-[#FFF8F4] flex items-center justify-center text-[#2173B7] mr-4 border border-[#F7D1B6]">
                                                        <FileText size={24}/> {/* Slightly larger icon */}
                                                    </div>
                                                    {/* Assignment Title (Blue) and Domain (Orange) */}
                                                    <div>
                                                        <h3 className="text-xl font-bold text-[#D67C2C] leading-tight">
                                                            {assignment.title || "Untitled Assignment"}
                                                        </h3>
                                                        <p className="text-sm text-[#2173B7] mt-1">
                                                            {assignment.domain || "General Assignment"}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Due Date (Orange) */}
                                                {assignment.due_date && (
                                                    <div className="flex items-center text-[#2173B7]">
                                                        <Clock className="h-4 w-4 mr-2 text-[#2173B7]"/>
                                                        <p className="text-sm">Due: {formatSimpleDate(assignment.due_date)}</p>
                                                    </div>
                                                )}

                                                {/* Hover Arrow */}
                                                <div
                                                    className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <ChevronRight size={24} className="text-[#2173B7]"/>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Attendance Overview */}
                        <div className="bg-white shadow-xl rounded-2xl overflow-hidden mb-8">
                            <div
                                className="px-4 py-5 sm:px-6 bg-[#FFF8F4] flex justify-between items-center border-b-2 border-[#F7D1B6]">
                                <div>
                                    <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Attendance
                                        Overview</h2>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        Visual summary of your child's attendance
                                    </p>
                                </div>
                                <Calendar className="h-8 w-8 text-[#2173B7]"/>
                            </div>

                            <div className="p-6">
                                {selectedChildAttendance.length === 0 ? (
                                    <div className="p-8 text-center text-gray-600 bg-gray-50 rounded-lg">
                                        <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4"/>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Attendance
                                            Records Yet!</h3>
                                        <p className="text-lg">
                                            Attendance data will appear here as records become available.
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Summary Cards Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                            {/* Overall Attendance Percentage Card (Graph Type) */}
                                            <div
                                                className="relative p-6 rounded-xl shadow-md border-2 border-[#F7D1B6] bg-white text-center flex flex-col items-center justify-center">
                                                <h3 className="text-lg font-bold text-[#D67C2C] mb-4">Overall
                                                    Attendance</h3>
                                                <div
                                                    className="relative w-32 h-32 rounded-full flex items-center justify-center font-bold text-2xl text-[#D67C2C]"
                                                    style={{
                                                        background: `conic-gradient(#4CAF50 ${stats.percentage}%, #E0E0E0 ${stats.percentage}%)`
                                                    }}
                                                >
                                                    <div
                                                        className="absolute w-28 h-28 rounded-full bg-white flex items-center justify-center">
                                                        <span
                                                            className="text-3xl font-extrabold text-[#2173B7]">{stats.percentage}%</span>
                                                    </div>
                                                </div>
                                                <p className="mt-4 text-sm text-gray-600">
                                                    Total Records: <span
                                                    className="font-semibold">{stats.total}</span>
                                                </p>
                                            </div>

                                            {/* Present Count Card */}
                                            <div
                                                className="p-6 rounded-xl shadow-md border-2 border-[#F7D1B6] bg-white flex flex-col items-start justify-between">
                                                <div className="flex items-center mb-3">
                                                    <div
                                                        className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                                        <CheckCircle size={24} className="text-green-600"/>
                                                    </div>
                                                    <h3 className="text-lg font-bold text-[#D67C2C]">Present
                                                        Days</h3>
                                                </div>
                                                <p className="text-4xl font-extrabold text-green-700">{stats.present}</p>
                                                <p className="text-sm text-gray-600 mt-2">Days your child was
                                                    present.</p>
                                            </div>

                                            {/* Absent Count Card */}
                                            <div
                                                className="p-6 rounded-xl shadow-md border-2 border-[#F7D1B6] bg-white flex flex-col items-start justify-between">
                                                <div className="flex items-center mb-3">
                                                    <div
                                                        className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                                                        <X size={24} className="text-red-600"/>
                                                    </div>
                                                    <h3 className="text-lg font-bold text-[#D67C2C]">Absent
                                                        Days</h3>
                                                </div>
                                                <p className="text-4xl font-extrabold text-red-700">{stats.absent}</p>
                                                <p className="text-sm text-gray-600 mt-2">Days your child was
                                                    absent.</p>
                                            </div>
                                        </div>

                                        {/* Recent Attendance List */}
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold text-[#D67C2C] mb-4">Recent
                                                Attendance Records</h3>
                                            <div
                                                className="rounded-lg border-2 border-[#F7D1B6] overflow-hidden shadow-sm">
                                                <table className="min-w-full divide-y divide-[#F7D1B6]">
                                                    <thead className="bg-[#FFF8F4]">
                                                    <tr>
                                                        <th scope="col"
                                                            className="px-6 py-3 text-left text-xs font-semibold text-[#2173B7] uppercase tracking-wider">
                                                            Date
                                                        </th>
                                                        <th scope="col"
                                                            className="px-6 py-3 text-left text-xs font-semibold text-[#2173B7] uppercase tracking-wider">
                                                            Status
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-[#F7D1B6]">
                                                    {selectedChildAttendance.slice(0, 5).map((record, index) => (
                                                        <tr key={index}
                                                            className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                                                                {formatSimpleDate(record.date)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                {record.present ? (
                                                                    <span
                                                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                                        <CheckCircle size={16} className="mr-1.5"/>
                                                                        Present
                                                                    </span>
                                                                ) : (
                                                                    <span
                                                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                                                        <X size={16} className="mr-1.5"/>
                                                                        Absent
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <ProfilePictureModal
                    showProfilePictureModal={showProfilePictureModal}
                    setShowProfilePictureModal={setShowProfilePictureModal}
                    childProfilePicture={childProfilePicture}
                    selectedChild={selectedChild}
                    handleProfilePictureUpload={handleProfilePictureUpload}
                    uploadingPicture={uploadingPicture}
                />
            </div>
        </div>
    );
};

export default ChildDetailView;